// SPDX-License-Identifier: AGPL-3.0-or-later

import Accessibility from '@app/features/accessibility/state/Accessibility';
import Theme from '@app/features/theme/state/Theme';
import ThemeLibrary from '@app/features/theme/state/ThemeLibrary';
import styles from '@app/features/ui/avatars/AvatarCostume.module.css';
import {applyContrastAwareHalo, readAvatarCostumeSurfaceColor} from '@app/features/ui/avatars/AvatarCostumeContrast';
import {
	costumeAvatarTopInElementRatio,
	costumeInkBottomRatio,
	costumeOriginBelowAvatarTopPx,
	costumeOverhangPx,
	getCostumeInkBox,
	measureAndCacheCostumeInkBox,
} from '@app/features/ui/avatars/AvatarCostumeGeometry';
import {
	type AvatarCostumeDescriptor,
	resolveAvatarCostume,
	subscribeAvatarCostumeResolversChanged,
} from '@app/features/ui/avatars/AvatarCostumeResolvers';
import {retainAvatarCostumeScrollFrom} from '@app/features/ui/avatars/AvatarCostumeScroll';
import Users from '@app/features/user/state/Users';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useCallback, useEffect, useId, useLayoutEffect, useRef, useState} from 'react';

const SVG_URL_ID = /url\(#([^)]+)\)/g;

function prefixCostumeSvgIds(svg: SVGSVGElement, prefix: string): void {
	if (svg.dataset.costumeIdPrefix === prefix) return;
	svg.dataset.costumeIdPrefix = prefix;
	const idMap = new Map<string, string>();
	const register = (el: Element) => {
		const id = el.getAttribute('id');
		if (id == null || id === '') return;
		const next = `${prefix}${id}`;
		idMap.set(id, next);
		el.setAttribute('id', next);
	};
	register(svg);
	for (const el of svg.querySelectorAll('[id]')) register(el);
	if (idMap.size === 0) return;

	const rewrite = (value: string): string => {
		let next = value.replace(SVG_URL_ID, (match, raw: string) => {
			const mapped = idMap.get(raw);
			return mapped == null ? match : `url(#${mapped})`;
		});
		if (next.startsWith('#')) {
			const mapped = idMap.get(next.slice(1));
			if (mapped != null) next = `#${mapped}`;
		}
		return next;
	};

	for (const el of [svg, ...svg.querySelectorAll('*')]) {
		for (const attr of [...el.attributes]) {
			const rewritten = rewrite(attr.value);
			if (rewritten !== attr.value) el.setAttribute(attr.name, rewritten);
		}
		if (el.tagName.toLowerCase() === 'style' && el.textContent != null) {
			const rewritten = rewrite(el.textContent);
			if (rewritten !== el.textContent) el.textContent = rewritten;
		}
	}
}

function costumeClassName(placement: AvatarCostumeDescriptor['placement']): string {
	switch (placement) {
		case 'topper':
			return styles.topper;
		case 'ring':
			return styles.ring;
		case 'face-overlay':
			return styles.faceOverlay;
	}
}

function costumeStyleVars(
	costume: AvatarCostumeDescriptor,
	size: number,
	layer?: 'base' | 'overhang',
): React.CSSProperties {
	const avatarTop = layer == null ? null : costumeAvatarTopInElementRatio(costume, size);
	const base = {
		'--avatar-costume-width-ratio': String(costume.widthRatio),
		'--avatar-costume-aspect-ratio': String(costume.aspectRatio),
		'--avatar-costume-offset-x': `${costume.offsetXRatio * 100}%`,
		'--avatar-costume-offset-y': `${costume.offsetYRatio * 100}%`,
		'--avatar-costume-flip-x': costume.flipHorizontal ? '-1' : '1',
		'--avatar-costume-overhang': String(costumeOverhangPx(costume, size)),
		'--avatar-costume-origin-below': String(costumeOriginBelowAvatarTopPx(costume, size)),
		'--avatar-costume-ink-bottom': String(costumeInkBottomRatio(costume)),
		...(avatarTop != null ? {'--avatar-costume-avatar-top': String(avatarTop)} : null),
		...(costume.minFit != null ? {'--avatar-costume-min-fit': String(costume.minFit)} : null),
	} as React.CSSProperties;
	if (costume.art === 'silhouette') {
		return {
			...base,
			'--avatar-costume-tint': costume.tint,
		} as React.CSSProperties;
	}
	if (costume.art === 'accented') {
		return {
			...base,
			'--costume-accent': costume.accent,
			'--costume-accent-dark': costume.accentDark,
			'--costume-accent-soft': costume.accentSoft ?? costume.accent,
		} as React.CSSProperties;
	}
	return base;
}

/** Touch theme deps so light/dark and custom CSS re-evaluate contrast halo. */
export function touchAvatarCostumeThemeDeps(): void {
	void Theme.effectiveTheme;
	void Accessibility.customThemeCss;
	void ThemeLibrary.revision;
}

let surfaceColorCached = false;
let surfaceColorValue: string | null = null;
let surfaceColorTheme: string | null = null;
let surfaceColorCustomCss: string | null = null;
let surfaceColorRevision = -1;
let surfaceColorRootClass = '';

/**
 * Shared surface colour for halo contrast, probed once per theme change.
 *
 * `readAvatarCostumeSurfaceColor` appends a probe element to the document root and
 * reads computed style off it, so calling it per costume made every message prepend
 * do one document style resolve per avatar. Every costume on screen resolves against
 * the same surface, so the answer is shared.
 *
 * The root class list is part of the key on purpose: the store values flip during the
 * React render pass, before the matching classes reach the DOM, so keying on them alone
 * could latch the pre-switch colour. Re-probing when the applied classes change closes
 * that window. Reading the three stores here also keeps observers subscribed to them.
 */
/**
 * Drop the cached probe after a DOM write that changes surface colours without moving any cache
 * key input. The holiday theme preset is one: it is neither user CSS nor a library theme, and its
 * style element lands in an effect, so keying on the preference alone would latch the pre-switch
 * colour in the same window the root class guard exists to close.
 */
export function invalidateAvatarCostumeSurfaceColor(): void {
	surfaceColorCached = false;
}

export function currentAvatarCostumeSurfaceColor(): string | null {
	const effectiveTheme: string = Theme.effectiveTheme;
	const customThemeCss = Accessibility.customThemeCss;
	const revision = ThemeLibrary.revision;
	const rootClass = typeof document === 'undefined' ? '' : document.documentElement.className;
	if (
		surfaceColorCached &&
		surfaceColorTheme === effectiveTheme &&
		surfaceColorCustomCss === customThemeCss &&
		surfaceColorRevision === revision &&
		surfaceColorRootClass === rootClass
	) {
		return surfaceColorValue;
	}
	surfaceColorValue = readAvatarCostumeSurfaceColor();
	surfaceColorTheme = effectiveTheme;
	surfaceColorCustomCss = customThemeCss;
	surfaceColorRevision = revision;
	surfaceColorRootClass = rootClass;
	surfaceColorCached = true;
	return surfaceColorValue;
}

export function renderAvatarCostumeArt(
	costume: AvatarCostumeDescriptor,
	size: number,
	dataFlxPrefix = 'ui.avatars.avatar-costume',
	artRef?: (node: SVGSVGElement | HTMLImageElement | null) => void,
	layer?: 'base' | 'overhang',
): React.ReactElement {
	const resolved = applyContrastAwareHalo(costume, currentAvatarCostumeSurfaceColor());
	const costumeStyle = costumeStyleVars(resolved, size, layer);
	const className = clsx(
		styles.costume,
		costumeClassName(resolved.placement),
		layer === 'base' && styles.costumeBase,
		layer === 'overhang' && styles.costumeOverhang,
		resolved.halo === 'none' && styles.noHalo,
		resolved.art === 'accented' && resolved.partHaloActive && styles.partHaloActive,
	);

	if (resolved.art === 'silhouette') {
		const Svg = resolved.Svg;
		return (
			<Svg
				ref={artRef}
				aria-hidden="true"
				className={clsx(className, styles.silhouette)}
				style={costumeStyle}
				data-flx={`${dataFlxPrefix}.silhouette`}
			/>
		);
	}

	if (resolved.art === 'accented') {
		const Svg = resolved.Svg;
		return (
			<Svg
				ref={artRef}
				aria-hidden="true"
				className={clsx(className, styles.accented)}
				style={costumeStyle}
				data-flx={`${dataFlxPrefix}.accented`}
			/>
		);
	}

	return (
		<img
			ref={artRef}
			src={resolved.src}
			alt=""
			aria-hidden="true"
			draggable={false}
			className={clsx(className, styles.fullColor)}
			style={costumeStyle}
			data-flx={`${dataFlxPrefix}.full-color`}
		/>
	);
}

export const AvatarCostume = observer(function AvatarCostume({
	userId,
	size,
}: {
	userId: string;
	size: number;
}): React.ReactElement | null {
	const [, setRevision] = useState(0);
	const [, setInkRevision] = useState(0);
	useEffect(() => subscribeAvatarCostumeResolversChanged(() => setRevision((revision) => revision + 1)), []);
	touchAvatarCostumeThemeDeps();
	// Touch wearer fields so USER_UPDATE re-renders costumes without a resolver notify.
	const user = Users.getUser(userId);
	void user?.avatarCostumeId;
	void user?.avatarCostumeColor;
	const costume = resolveAvatarCostume({userId, size});
	const instanceId = useId();
	const idPrefix = `c${instanceId.replace(/[^a-zA-Z0-9]/g, '')}`;
	const splitOverhang = costume?.scaleAboveAvatar === true;
	const artNodeRef = useRef<Element | null>(null);
	const bindLayer = useCallback(
		(layer: 'base' | 'overhang') => (node: SVGSVGElement | HTMLImageElement | null) => {
			if (node instanceof SVGSVGElement) {
				prefixCostumeSvgIds(node, `${idPrefix}${layer}-`);
			}
			if (layer === 'overhang') {
				artNodeRef.current = node;
				if (node == null || costume == null || !(node instanceof SVGSVGElement)) return;
				if (costume.inkTopRatio != null || costume.inkBottomRatio != null) return;
				if (getCostumeInkBox(costume.id) != null) return;
				const measured = measureAndCacheCostumeInkBox(costume.id, node);
				if (measured != null) setInkRevision((revision) => revision + 1);
			}
		},
		[costume, idPrefix],
	);
	const setArtRef = useCallback(
		(node: SVGSVGElement | HTMLImageElement | null) => {
			artNodeRef.current = node;
			if (node == null || costume == null || !(node instanceof SVGSVGElement)) return;
			if (costume.inkTopRatio != null || costume.inkBottomRatio != null) return;
			if (getCostumeInkBox(costume.id) != null) return;
			const measured = measureAndCacheCostumeInkBox(costume.id, node);
			if (measured != null) setInkRevision((revision) => revision + 1);
		},
		[costume],
	);
	useLayoutEffect(() => {
		if (costume == null) return;
		const node = artNodeRef.current;
		if (node == null) return;
		return retainAvatarCostumeScrollFrom(node);
	}, [costume?.id]);
	if (costume == null) return null;
	// Touch ink cache so a measurement bump re-reads overhang on the next render.
	void getCostumeInkBox(costume.id);
	if (splitOverhang) {
		const avatarTop = costumeAvatarTopInElementRatio(costume, size);
		const showBase = avatarTop == null || avatarTop < 1;
		const showOverhang = avatarTop == null || avatarTop > 0;
		return (
			<>
				{showBase
					? renderAvatarCostumeArt(costume, size, 'ui.avatars.avatar-costume', bindLayer('base'), 'base')
					: null}
				{showOverhang
					? renderAvatarCostumeArt(costume, size, 'ui.avatars.avatar-costume', bindLayer('overhang'), 'overhang')
					: null}
			</>
		);
	}
	return renderAvatarCostumeArt(costume, size, 'ui.avatars.avatar-costume', setArtRef);
});
